import { useRef, useState } from "react";
import { Download, FileText, Loader2, Pause, PlayCircle, Sparkles, Trash2, Upload, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { Button } from "./ui/button";

const RTL = ["Urdu", "Punjabi (Shahmukhi)"];
export const translated = (langs) => (langs || []).filter((l) => l !== "English");

export const Tr = ({ lang, children, className = "" }) => {
  const rtl = RTL.includes(lang);
  const cls = rtl ? "urdu" : lang === "Punjabi (Gurmukhi)" ? "gurmukhi" : "";
  return <p dir={rtl ? "rtl" : "ltr"} className={`${cls} ${className}`}>{children}</p>;
};

const save = async (url, name, err) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
  } catch (e) { toast.error(err); }
};
export const downloadTalk = (id, lang) => save(`/training/talks/${id}/pdf?lang=${encodeURIComponent(lang)}`, `Toolbox_Talk_${id}_${lang.replace(/[^A-Za-z]+/g, "_")}.pdf`, "Could not create the handout PDF");
export const downloadSop = (t) => save(`/training/talks/${t.id}/sop`, t.sop?.filename || t.sop_file, "Could not download the SOP");
export const isReady = (t) => !t.status || t.status === "published" || t.status === "draft";

const audioCache = new Map();
let current = null;
export const ListenButton = ({ talkId, lang, testid, url }) => {
  const [state, setState] = useState("idle");
  const play = async () => {
    if (state === "playing") { current?.pause(); setState("idle"); return; }
    const key = url || `${talkId}|${lang}`;
    setState("loading");
    try {
      if (!audioCache.has(key)) {
        const r = await api.get(url || `/training/talks/${talkId}/audio?lang=${encodeURIComponent(lang)}`, { responseType: "blob", timeout: 120000 });
        audioCache.set(key, URL.createObjectURL(r.data));
      }
      current?.pause();
      current = new Audio(audioCache.get(key));
      current.onended = () => setState("idle");
      current.onpause = () => setState("idle");
      await current.play();
      setState("playing");
    } catch (e) { setState("idle"); toast.error("Read aloud is not available right now - please try again"); }
  };
  const Icon = state === "loading" ? Loader2 : state === "playing" ? Pause : Volume2;
  return (
    <Button type="button" variant="outline" className="border-cyan-700 bg-transparent text-cyan-200" onClick={play} disabled={state === "loading"} data-testid={testid}>
      <Icon className={`mr-1 h-4 w-4 ${state === "loading" ? "animate-spin" : ""}`} />{state === "playing" ? "Stop" : state === "loading" ? "Preparing audio..." : url ? "Listen" : `Listen (${lang})`}
    </Button>
  );
};

export const LangSelect = ({ langs, value, onChange, testid }) => (
  <select className="field w-auto" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {langs.map((l) => <option key={l}>{l}</option>)}
  </select>
);

export const TalkPoints = ({ points, lang }) => (
  <ol className="divide-y divide-slate-800 text-sm" data-testid="talk-points">
    {points.map((p, i) => (
      <li key={i} className="grid gap-2 py-2 sm:grid-cols-2">
        <span><span className="mr-2 font-mono text-xs text-slate-500">{i + 1}</span>{p.en}</span>
        {lang && p.tr?.[lang] && <Tr lang={lang} className="text-base">{p.tr[lang]}</Tr>}
      </li>
    ))}
  </ol>
);

const SopUpload = ({ t, onSaved }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd);
      await api.put(`/training/talks/${t.id}/sop`, { path: up.data.path, filename: file.name });
      toast.success("SOP document updated");
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`talk-${t.id}-sop-input`} />
      <Button variant="ghost" disabled={busy} onClick={() => ref.current?.click()} data-testid={`talk-${t.id}-sop-upload`}><Upload className="mr-1 h-4 w-4" />{busy ? "Uploading..." : "Replace SOP"}</Button>
    </>
  );
};

const TalkQuiz = ({ quiz, lang }) => (
  <div className="mt-2 space-y-2 text-sm">
    {quiz.map((q, i) => (
      <div key={i} className="grid gap-2 rounded border border-slate-800 p-2 sm:grid-cols-2">
        <span className="font-semibold">{i + 1}. {q.q}</span><Tr lang={lang} className="font-semibold">{q.tr[lang].q}</Tr>
      </div>
    ))}
  </div>
);

const PendingCard = ({ t, canManage, onSaved }) => (
  <div className="panel flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`talk-${t.id}`}>
    <div>
      <p className="eyebrow">From {t.sop?.filename}</p>
      {t.status === "generating"
        ? <p className="flex items-center gap-2 text-sm" data-testid={`talk-${t.id}-generating`}><Loader2 className="h-4 w-4 animate-spin" />Drafting the talk, quiz and 5 translations. This takes 1-3 minutes.</p>
        : <p className="text-sm text-red-300" data-testid={`talk-${t.id}-failed`}>Could not draft this talk: {t.error}</p>}
    </div>
    {canManage && t.status !== "generating" && <DeleteTalk t={t} onSaved={onSaved} />}
    {canManage && t.status === "generating" && (Date.now() - new Date(t.created_at)) > 10 * 60000 && <DeleteTalk t={t} onSaved={onSaved} />}
  </div>
);

const DeleteTalk = ({ t, onSaved }) => (
  <Button variant="ghost" className="text-red-300" onClick={async () => { if (!window.confirm(`Delete "${t.title}"?`)) return; try { await api.delete(`/training/talks/${t.id}`); toast.success("Talk deleted"); onSaved(); } catch (e) { toast.error(fmtErr(e)); } }} data-testid={`talk-${t.id}-delete`}>
    <Trash2 className="mr-1 h-4 w-4" />Delete
  </Button>
);

const DraftBar = ({ t, onSaved }) => (
  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-sm" data-testid={`talk-${t.id}-draft`}>
    <span>AI draft - check the content and translations, then publish so trainers can deliver it.</span>
    <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={async () => { try { await api.post(`/training/talks/${t.id}/publish`); toast.success("Talk published"); onSaved(); } catch (e) { toast.error(fmtErr(e)); } }} data-testid={`talk-${t.id}-publish`}>Publish</Button>
  </div>
);

const TalkCard = ({ t, canStart, canManage, onStart, onSaved }) => {
  const langs = translated(t.languages);
  const [lang, setLang] = useState(langs[0]);
  return (
    <div className="panel p-4" data-testid={`talk-${t.id}`}>
      {t.status === "draft" && canManage && <DraftBar t={t} onSaved={onSaved} />}
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow">About {t.duration_mins} min · {t.languages.length} languages{t.ai ? " · AI-drafted from SOP" : ""}</p>
          <h3 className="font-display text-xl font-bold uppercase">{t.title}</h3>
          <Tr lang={lang} className="text-lg text-slate-300 !text-left">{t.title_tr[lang]}</Tr>
          <button onClick={() => downloadSop(t)} className="mt-1 flex items-center gap-1 text-left text-xs text-cyan-300 hover:underline" data-testid={`talk-${t.id}-sop`}>
            <FileText className="h-3.5 w-3.5 shrink-0" />{t.sop_ref} · {t.sop?.filename} ({t.sop?.uploaded_by})
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LangSelect langs={langs} value={lang} onChange={setLang} testid={`talk-${t.id}-lang`} />
          <ListenButton talkId={t.id} lang={lang} testid={`talk-${t.id}-listen`} />
          <Button variant="outline" className="border-slate-700 bg-transparent" onClick={() => downloadTalk(t.id, lang)} data-testid={`talk-${t.id}-pdf`}><Download className="mr-1 h-4 w-4" />Handout PDF</Button>
          {canManage && <SopUpload t={t} onSaved={onSaved} />}
          {canManage && t.custom && <DeleteTalk t={t} onSaved={onSaved} />}
          {canStart && t.status !== "draft" && <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => onStart(t)} data-testid={`talk-${t.id}-start`}><PlayCircle className="mr-1 h-4 w-4" />Deliver this talk</Button>}
        </div>
      </div>
      <details>
        <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-100" data-testid={`talk-${t.id}-preview`}>Show talk content and {t.quiz.length} quiz questions in English / {lang}</summary>
        <TalkPoints points={t.points} lang={lang} />
        <TalkQuiz quiz={t.quiz} lang={lang} />
      </details>
    </div>
  );
};

const CreateFromSop = ({ onSaved }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const go = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/training/talks/generate", fd, { timeout: 120000 });
      toast.success("Drafting the talk - it will appear below in 1-3 minutes");
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => go(e.target.files[0])} data-testid="create-talk-input" />
      <Button className="bg-rose-600 hover:bg-rose-700" disabled={busy} onClick={() => ref.current?.click()} data-testid="create-talk-btn"><Sparkles className="mr-1 h-4 w-4" />{busy ? "Uploading..." : "Create talk from SOP"}</Button>
    </>
  );
};

export const TalkLibrary = ({ talks, canStart, canManage, onStart, onSaved }) => (
  <div className="space-y-3" data-testid="talk-library">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <p className="max-w-3xl text-sm text-slate-400">Toolbox talks based on your WRM SOPs, in English, Urdu, Polish, Romanian and Punjabi. Workers can read or listen to the talk in their language, answer the quiz and sign in the app.</p>
      {canManage && <CreateFromSop onSaved={onSaved} />}
    </div>
    {talks.map((t) => (isReady(t) ? <TalkCard key={t.id} t={t} canStart={canStart} canManage={canManage} onStart={onStart} onSaved={onSaved} /> : <PendingCard key={t.id} t={t} canManage={canManage} onSaved={onSaved} />))}
  </div>
);
